import React, { useState, useEffect, useRef } from 'react';
import { MoreVertical, Loader2 } from 'lucide-react';

/**
 * ActionMenu — ⋮ button that opens a popover with action items.
 *
 * items: Array of { label, Icon, color, bg, onClick, loading, disabled, hidden }
 *   hidden: true  → item is not rendered (conditional action)
 *   disabled: true → greyed out, not clickable
 *   loading: true  → shows spinner next to label
 */
export default function ActionMenu({ items = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const visible = items.filter(i => !i.hidden);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: 32, height: 32, borderRadius: 8,
          border: '1.5px solid #e8e8f1',
          background: open ? '#f1ebff' : '#fff',
          color: open ? '#7c3aed' : '#6e6e86',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all .12s',
        }}
        title="Actions"
      >
        <MoreVertical size={15} strokeWidth={2} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 36, zIndex: 9999,
          background: '#fff', border: '1.5px solid #e8e8f1',
          borderRadius: 10, boxShadow: '0 6px 24px rgba(20,20,43,.12)',
          minWidth: 160, padding: '4px 0', overflow: 'hidden',
        }}>
          {visible.map((item, idx) => (
            item.divider ? (
              <div key={idx} style={{ height: 1, background: '#f0f0f6', margin: '4px 0' }} />
            ) : (
              <button
                key={idx}
                onClick={() => { if (!item.disabled && !item.loading) { item.onClick?.(); setOpen(false); } }}
                disabled={item.disabled || item.loading}
                style={{
                  width: '100%', padding: '9px 14px',
                  background: 'none', border: 'none', cursor: item.disabled || item.loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 9,
                  fontSize: 13, fontWeight: 600,
                  color: item.disabled ? '#b0b0c8' : (item.color || '#14142b'),
                  fontFamily: 'inherit',
                  opacity: item.disabled ? 0.5 : 1,
                  transition: 'background .1s',
                }}
                onMouseEnter={e => { if (!item.disabled && !item.loading) e.currentTarget.style.background = item.bg || '#f4f4f9'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                {item.loading
                  ? <Loader2 size={13} style={{ animation: 'spin .8s linear infinite', flexShrink: 0 }} />
                  : item.Icon && <item.Icon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                }
                {item.label}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}
